import { useState, useEffect } from 'react';
import { categoryAPI, IMAGE_BASE } from '../../services/api';

export default function CategoriesAdmin() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editCat, setEditCat] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', isActive: true, parentId: '', isParent: true });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [formBusy, setFormBusy] = useState(false);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const res = await categoryAPI.getAll();
            setCategories(res.data || []);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { fetchCategories(); }, []);

    // Get only parent (top-level) categories for dropdown
    const parentCategories = categories.filter(c => !c.parentId);

    // Build hierarchical list: parent followed by its children
    const hierarchicalCategories = [];
    parentCategories.forEach(parent => {
        hierarchicalCategories.push(parent);
        const subs = categories.filter(c => c.parentId === parent.id);
        subs.forEach(sub => hierarchicalCategories.push(sub));
    });
    // Also include orphan categories (parentId set but parent not found - edge case)
    const listedIds = new Set(hierarchicalCategories.map(c => c.id));
    categories.forEach(c => {
        if (!listedIds.has(c.id)) hierarchicalCategories.push(c);
    });

    const openCreate = (presetParentId = '') => {
        setEditCat(null);
        setForm({ name: '', description: '', isActive: true, parentId: presetParentId, isParent: !presetParentId });
        setImageFile(null);
        setImagePreview(null);
        setShowModal(true);
    };

    const openAddSub = (parentId) => {
        openCreate(String(parentId));
    };

    const openEdit = (cat) => {
        setEditCat(cat);
        setForm({ name: cat.name, description: cat.description || '', isActive: cat.isActive, parentId: cat.parentId || '', isParent: !cat.parentId });
        setImageFile(null);
        setImagePreview(cat.image ? IMAGE_BASE + cat.image : null);
        setShowModal(true);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e, keepOpen = false) => {
        e.preventDefault();
        setFormBusy(true);
        try {
            const fd = new FormData();
            fd.append('name', form.name);
            fd.append('description', form.description);
            fd.append('isActive', form.isActive);
            fd.append('parentId', form.isParent ? '' : (form.parentId || ''));
            if (imageFile) fd.append('image', imageFile);

            if (editCat) {
                await categoryAPI.update(editCat.id, fd);
                setShowModal(false);
            } else {
                await categoryAPI.create(fd);
                if (keepOpen) {
                    // Reset form but keep parentId selected
                    const currentParentId = form.parentId;
                    setForm({ name: '', description: '', isActive: true, parentId: currentParentId, isParent: !currentParentId });
                    setImageFile(null);
                    setImagePreview(null);
                } else {
                    setShowModal(false);
                }
            }
            fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Hata oluştu');
        }
        setFormBusy(false);
    };

    const handleDelete = async (id) => {
        if (!confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) return;
        try {
            await categoryAPI.delete(id);
            fetchCategories();
        } catch (err) {
            const data = err.response?.data;
            if (data?.hasSubcategories) {
                if (confirm(`Bu kategorinin ${data.subCount} alt kategorisi var. Hepsi silinecek. Devam edilsin mi?`)) {
                    try {
                        await categoryAPI.delete(id, { params: { force: 'true' } });
                        fetchCategories();
                    } catch (err2) {
                        alert(err2.response?.data?.error || 'Silinemedi');
                    }
                }
            } else {
                alert(data?.error || 'Silinemedi');
            }
        }
    };

    // Filter parent categories available for dropdown (exclude current category if editing)
    const availableParents = parentCategories.filter(c => !editCat || c.id !== editCat.id);

    return (
        <div>
            <div className="admin-header">
                <h1>Kategori Yönetimi</h1>
                <button className="btn btn-primary" onClick={openCreate}>+ Yeni Kategori</button>
            </div>

            {loading ? <div className="loading"><div className="spinner"></div></div> : (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Kategori Adı</th>
                                <th>Slug</th>
                                <th>Ürün Sayısı</th>
                                <th>Durum</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {hierarchicalCategories.map(cat => (
                                <tr key={cat.id}>
                                    <td style={{ fontWeight: cat.parentId ? 400 : 600 }}>
                                        {cat.parentId && (
                                            <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>└ </span>
                                        )}
                                        {cat.name}
                                    </td>
                                    <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{cat.slug}</td>
                                    <td>{cat.productCount || 0}</td>
                                    <td>
                                        <span className={`stock-badge ${cat.isActive ? 'stock-in' : 'stock-out'}`}>
                                            {cat.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            {!cat.parentId && (
                                                <button className="btn-icon" title="Alt kategori ekle" onClick={() => openAddSub(cat.id)} style={{ color: 'var(--primary)' }}>+</button>
                                            )}
                                            <button className="btn-icon" onClick={() => openEdit(cat)}>✏️</button>
                                            <button className="btn-icon" onClick={() => handleDelete(cat.id)} style={{ color: 'var(--danger)' }}>🗑️</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className="modal" style={{ maxWidth: 500 }}>
                        <div className="modal-header">
                            <h2>{editCat ? 'Kategori Düzenle' : 'Yeni Kategori'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '0.5rem' }}>
                                        <input type="checkbox" checked={form.isParent} onChange={e => setForm({ ...form, isParent: e.target.checked, parentId: e.target.checked ? '' : form.parentId })} /> Üst Kategori (Ana Kategori)
                                    </label>
                                    {!form.isParent && (
                                        <select className="form-control" value={form.parentId} onChange={e => setForm({ ...form, parentId: e.target.value })}>
                                            <option value="">Üst kategori seçin...</option>
                                            {availableParents.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>Kategori Adı *</label>
                                    <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label>Açıklama</label>
                                    <textarea className="form-control" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Aktif
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
                                {!editCat && (
                                    <button type="button" className="btn btn-secondary" disabled={formBusy} onClick={(e) => handleSubmit(e, true)}>
                                        {formBusy ? '...' : 'Kaydet & Yeni Ekle'}
                                    </button>
                                )}
                                <button type="submit" className="btn btn-primary" disabled={formBusy}>
                                    {formBusy ? 'Kaydediliyor...' : (editCat ? 'Güncelle' : 'Ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
